import React from 'react';
import { TrendingUp, Award, Loader2, Check, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import type { OptimizationStrategy } from '../../utils/scenario-optimizer';

interface ScenarioOptimizerProps {
  onSelectStrategy: (strategy: OptimizationStrategy) => Promise<void>;
  onSelectCustom: () => void;
  isLoading?: boolean;
  progress?: number;
  progressMessage?: string;
  compact?: boolean;
  currentStrategy?: OptimizationStrategy;
}

export function ScenarioOptimizer({
  onSelectStrategy,
  onSelectCustom,
  isLoading = false,
  progress = 0,
  progressMessage = '',
  compact = false,
  currentStrategy,
}: ScenarioOptimizerProps) {
  const [activeStrategy, setActiveStrategy] = React.useState<OptimizationStrategy | null>(null);

  const handleSelectStrategy = async (strategy: OptimizationStrategy) => {
    setActiveStrategy(strategy);
    try {
      await onSelectStrategy(strategy);
    } finally {
      setActiveStrategy(null);
    }
  };

  if (compact) {
    return (
      <div className="space-y-2">
        {currentStrategy !== 'best_label' && (
          <Button
            onClick={() => handleSelectStrategy('best_label')}
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-start"
            size="sm"
          >
            {isLoading && activeStrategy === 'best_label' ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <Award className="w-4 h-4 mr-2" />
                Meilleure lettre
              </>
            )}
          </Button>
        )}

        {currentStrategy !== 'best_value' && (
          <Button
            onClick={() => handleSelectStrategy('best_value')}
            disabled={isLoading}
            className="w-full bg-green-600 hover:bg-green-700 text-white justify-start"
            size="sm"
          >
            {isLoading && activeStrategy === 'best_value' ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4 mr-2" />
                Meilleure rentabilité
              </>
            )}
          </Button>
        )}

        <Button
          onClick={onSelectCustom}
          disabled={isLoading}
          variant="outline"
          className="w-full justify-start"
          size="sm"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Sur mesure
        </Button>
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {activeStrategy === 'best_label' ? 'Meilleure lettre possible' : 'Meilleure rentabilité'}
              </h3>
              <p className="text-gray-600 mb-6">
                {progressMessage || 'Analyse en cours...'}
              </p>

              <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500">{progress}%</p>

              <p className="text-xs text-gray-400 mt-4">
                Cette opération peut prendre quelques secondes...
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Besoin d'aide pour choisir vos travaux ?
        </h2>
        <p className="text-gray-600 mb-6">
          Laissez-nous vous proposer les scénarios les plus adaptés à vos objectifs
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-white hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-400">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Meilleure lettre</h3>
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mt-1">
                    Rénovation complète
                  </span>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Performance énergétique maximale
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Classe énergétique optimale</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Confort thermique maximal</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Valorisation du bien</span>
                </li>
              </ul>

              <Button
                onClick={() => handleSelectStrategy('best_label')}
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLoading && activeStrategy === 'best_label' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  'Générer ce scénario'
                )}
              </Button>
            </div>
          </Card>

          <Card className="bg-white hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-400">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Meilleure rentabilité</h3>
                  <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded mt-1">
                    Rénovation ciblée
                  </span>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Optimiser votre investissement
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Meilleur retour sur investissement</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Économies immédiates</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Budget maîtrisé</span>
                </li>
              </ul>

              <Button
                onClick={() => handleSelectStrategy('best_value')}
                disabled={isLoading}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {isLoading && activeStrategy === 'best_value' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  'Générer ce scénario'
                )}
              </Button>
            </div>
          </Card>

          <Card className="bg-white hover:shadow-lg transition-shadow border-2 border-transparent hover:border-gray-400">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Sur mesure</h3>
                  <span className="inline-block bg-gray-100 text-gray-800 text-xs font-semibold px-2 py-1 rounded mt-1">
                    Personnalisé
                  </span>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Créez votre propre scénario
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Choisissez vos travaux</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Adaptez à vos priorités</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Contrôle total</span>
                </li>
              </ul>

              <Button
                onClick={onSelectCustom}
                disabled={isLoading}
                variant="outline"
                className="w-full"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Créer manuellement
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
